import React, { createContext, useState, useEffect } from "react";
import api from "../api/axios";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [vendorProfile, setVendorProfile] = useState(null);
  const [agentProfile, setAgentProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load from localStorage on app start
  useEffect(() => {
    try {
      const savedUser = localStorage.getItem("user");
      const savedVendorProfile = localStorage.getItem("vendorProfile");
      const savedAgentProfile = localStorage.getItem("agentProfile");

      if (savedUser) {
        setUser(JSON.parse(savedUser));
      }

      if (savedVendorProfile) {
        setVendorProfile(JSON.parse(savedVendorProfile));
      }

      if (savedAgentProfile) {
        setAgentProfile(JSON.parse(savedAgentProfile));
      }
    } catch (e) {
      console.error("Failed to restore auth state:", e);
      localStorage.removeItem("user");
      localStorage.removeItem("vendorProfile");
      localStorage.removeItem("agentProfile");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Login
   */
  const login = (userObject, vendorProfileObject = null, agentProfileObject = null) => {
    if (!userObject || typeof userObject !== "object") {
      console.error("Invalid user object:", userObject);
      return false;
    }

    setUser(userObject);
    localStorage.setItem("user", JSON.stringify(userObject));

    if (vendorProfileObject && userObject?.role === "vendor") {
      setVendorProfile(vendorProfileObject);
      localStorage.setItem(
        "vendorProfile",
        JSON.stringify(vendorProfileObject)
      );
    } else {
      setVendorProfile(null);
      localStorage.removeItem("vendorProfile");
    }

    if (agentProfileObject && userObject?.role === "agent") {
      setAgentProfile(agentProfileObject);
      localStorage.setItem(
        "agentProfile",
        JSON.stringify(agentProfileObject)
      );
    } else {
      setAgentProfile(null);
      localStorage.removeItem("agentProfile");
    }

    return true;
  };

  /**
   * Logout
   */
  const logout = async () => {
    try {
      await api.post("/auth/logout", {}, { withCredentials: true });
    } catch (err) {
      // Ignore errors — still clear local state
    }
    setUser(null);
    setVendorProfile(null);
    setAgentProfile(null);
    localStorage.removeItem("user");
    localStorage.removeItem("vendorProfile");
    localStorage.removeItem("agentProfile");
    localStorage.removeItem("token");
    localStorage.removeItem("accessToken");
  };

  /**
   * Update user
   */
  const updateUser = (updatedData, replace = false) => {
    if (!user && !replace) return false;

    const updated = replace
      ? updatedData
      : { ...user, ...updatedData };

    setUser(updated);
    localStorage.setItem("user", JSON.stringify(updated));

    return true;
  };

  /**
   * Update vendor profile
   */
  const updateVendorProfile = (updatedData, replace = false) => {
    if (!vendorProfile && !replace) return false;

    const updated = replace
      ? updatedData
      : { ...vendorProfile, ...updatedData };

    setVendorProfile(updated);
    localStorage.setItem(
      "vendorProfile",
      JSON.stringify(updated)
    );

    return true;
  };

  const updateAgentProfile = (updatedData, replace = false) => {
    if (!agentProfile && !replace) return false;

    const updated = replace
      ? updatedData
      : { ...agentProfile, ...updatedData };

    setAgentProfile(updated);
    localStorage.setItem(
      "agentProfile",
      JSON.stringify(updated)
    );

    return true;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        vendorProfile,
        agentProfile,
        login,
        logout,
        updateUser,
        updateVendorProfile,
        updateAgentProfile,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};